import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../Navbar/Navbar';
import '../Dashboard/Dashboard.css';
import { FaCamera, FaUpload, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
const getInitials = (name) =>
  name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : 'A';

const BACKEND = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://api.leave-management.manurevasolutions.com'
    : 'http://localhost:5000');

/* ── localStorage cache helpers ───────────────────────── */
const CACHE_PIC  = 'topbar_profile_pic';
const CACHE_USER = 'topbar_current_user';

const getCachedPic  = () => { try { return localStorage.getItem(CACHE_PIC) || null; }  catch { return null; } };
const getCachedUser = () => { try { return JSON.parse(localStorage.getItem(CACHE_USER)); } catch { return null; } };
const saveCachedPic  = (v) => { try { if (v) localStorage.setItem(CACHE_PIC, v); else localStorage.removeItem(CACHE_PIC); } catch {} };
const saveCachedUser = (u) => { try { if (u) localStorage.setItem(CACHE_USER, JSON.stringify(u)); else localStorage.removeItem(CACHE_USER); } catch {} };

const TopBar = ({ currentUser: propUser, profilePic: propPic }) => {

  const [menuOpen,        setMenuOpen]        = useState(false);
  const [currentUser,     setCurrentUser]     = useState(propUser || getCachedUser() || null);
  const [profilePic,      setProfilePic]      = useState(propPic  || getCachedPic()  || null);
  const [userLoaded,      setUserLoaded]      = useState(!!(propUser || getCachedUser()));
  const [picLoaded,       setPicLoaded]       = useState(!!(propPic  || getCachedPic()));
  const [notifCount,      setNotifCount]      = useState(0);
  const [notifOpen,       setNotifOpen]       = useState(false);
  const [pendingList,     setPendingList]     = useState([]);
  const [isMobile,        setIsMobile]        = useState(window.innerWidth <= 768);

  /* ── profile modal states ────────────────────────────── */
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [previewImage,     setPreviewImage]     = useState(null);
  const [isUploading,      setIsUploading]      = useState(false);

  const notifRef          = useRef(null);
  const profileFileInputRef = useRef(null);
  const filePickerOpenRef   = useRef(false);

  /* ── responsive listener ─────────────────────────────── */
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  /* ── fetch pending approvals ──────────────────────────── */
  const fetchNotifCount = () => {
    fetch(`${BACKEND}/api/manager/pending-team-approvals`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setNotifCount(list.length);
        setPendingList(list);
      })
      .catch(() => {});
  };

  /* ── close panel when clicking outside ───────────────── */
  // useEffect(() => {
  //   const handleClick = (e) => {
  //     if (notifRef.current && !notifRef.current.contains(e.target)) {
  //       setNotifOpen(false);
  //     }
  //   };
  //   document.addEventListener('mousedown', handleClick);
  //   return () => document.removeEventListener('mousedown', handleClick);
  // }, []);

  /* ── init ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!propUser) {
      fetch(`${BACKEND}/api/user`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(u => {
          if (u) { setCurrentUser(u); saveCachedUser(u); }
          setUserLoaded(true);
        })
        .catch(() => setUserLoaded(true));
    }
    if (!propPic) {
      fetch(`${BACKEND}/api/user/profile-picture`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          const pic = d?.profile_picture || null;
          setProfilePic(pic);
          saveCachedPic(pic);
        })
        .catch(() => {})
        .finally(() => setPicLoaded(true));
    }

    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, 10000);

    window.addEventListener('leaveRequestApproved', fetchNotifCount);
    window.addEventListener('leaveRequestRejected', fetchNotifCount);

    return () => {
      clearInterval(interval);
      window.removeEventListener('leaveRequestApproved', fetchNotifCount);
      window.removeEventListener('leaveRequestRejected', fetchNotifCount);
    };
  }, []);

  /* ── profile picture handlers ────────────────────────── */
  const handleProfileFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      event.target.value = null;
      Swal.fire({
        html: `
          <div style="padding: 1rem 0.5rem">
            <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </div>
            <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Invalid file type</p>
            <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">Please select an image file (JPG, PNG, GIF, etc.)</p>
          </div>
        `,
        width: '340px',
        confirmButtonText: 'OK',
        confirmButtonColor: '#111111',
        customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      event.target.value = null;
      Swal.fire({
        html: `
          <div style="padding: 1rem 0.5rem">
            <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </div>
            <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">File too large</p>
            <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">File size must be less than <strong>5MB</strong>. Please choose a smaller image.</p>
          </div>
        `,
        width: '340px',
        confirmButtonText: 'OK',
        confirmButtonColor: '#111111',
        customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
      setTimeout(() => { filePickerOpenRef.current = false; }, 300);
    };
    reader.readAsDataURL(file);
    event.target.value = null;
  };

 const handleUploadProfilePicture = async () => {
  if (!previewImage) return;
  setIsUploading(true);
  try {
    const response = await fetch(`${BACKEND}/api/user/profile-picture`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile_picture: previewImage })
    });

    if (response.ok) {
      saveCachedPic(previewImage);
      setProfilePic(previewImage);
      setPicLoaded(true);
      setShowProfileModal(false);
      setPreviewImage(null);
    } else {
      Swal.fire({
        html: `
          <div style="padding: 1rem 0.5rem">
            <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </div>
            <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Upload Failed</p>
            <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">Failed to upload profile picture. Please try again.</p>
          </div>
        `,
        width: '340px',
        confirmButtonText: 'Close',
        confirmButtonColor: '#111111',
        customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
      });
    }
  } catch (err) {
    Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Upload Failed</p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">${err.message || 'Something went wrong. Please try again.'}</p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'Close',
      confirmButtonColor: '#111111',
      customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
    });
  } finally {
    setIsUploading(false);
  }
};

 const handleRemoveProfilePicture = async () => {
  setIsUploading(true);
  try {
    const response = await fetch(`${BACKEND}/api/user/profile-picture`, {
      method: 'DELETE',
      credentials: 'include'
    });

    if (response.ok) {
      saveCachedPic(null);
      setProfilePic(null);
      setPicLoaded(true);
      setShowProfileModal(false);
      setPreviewImage(null);
    } else {
      Swal.fire({
        html: `
          <div style="padding: 1rem 0.5rem">
            <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </div>
            <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Remove Failed</p>
            <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">Failed to remove profile picture. Please try again.</p>
          </div>
        `,
        width: '340px',
        confirmButtonText: 'Close',
        confirmButtonColor: '#111111',
        customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
      });
    }
  } catch (err) {
    Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Remove Failed</p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">${err.message || 'Something went wrong. Please try again.'}</p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'Close',
      confirmButtonColor: '#111111',
      customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
    });
  } finally {
    setIsUploading(false);
  }
};

  const closeProfileModal = () => {
    if (filePickerOpenRef.current) return;
    setShowProfileModal(false);
    setPreviewImage(null);
  };

  /* ── render avatar ───────────────────────────────────── */
  const renderAvatar = () => {
    // Always show something — never return null (prevents layout shift)
    if (picLoaded && profilePic) {
      return (
        <img
          src={profilePic}
          alt="avatar"
          style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      );
    }
    // Show initials if user loaded, or neutral placeholder while loading
    return (
      <span style={{ opacity: userLoaded ? 1 : 0.4, transition: 'opacity 0.3s' }}>
        {userLoaded ? getInitials(currentUser?.name) : 'A'}
      </span>
    );
  };

  /* ── leave type label ─────────────────────────────────── */
  const leaveLabel = (type) => {
    if (!type) return 'Leave';
    const m = {
      casual: 'Casual Leave', sick: 'Medical Leave', medical: 'Medical Leave',
      emergency: 'Permission', permission: 'Permission',
    };
    return m[type.toLowerCase()] || type;
  };

  /* ── priority color ───────────────────────────────────── */
  const prioColor = (prio) => {
    if (!prio) return '#6b7280';
    if (prio === 'high')   return '#ef4444';
    if (prio === 'medium') return '#f59e0b';
    return '#22c55e';
  };

  /* ── render ───────────────────────────────────────────── */
  return (
    <>
      <header className="adash-topbar">
        <button className="adash-hamburger" onClick={() => setMenuOpen(!menuOpen)}>
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
            <path d="M3 12h18M3 6h18M3 18h18" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        <span className="adash-topbar-brand">Manureva Digital Solutions</span>

        <div className="adash-topbar-right">
          {/* search */}
          {/* <div className="adash-topbar-search">
            <svg width="14" height="14" fill="none" viewBox="0 0 20 20">
              <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM18 18l-4.35-4.35" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input placeholder="Search…" readOnly />
          </div> */}

          {/* ── bell + panel ── */}
          <div ref={notifRef} style={{ position: 'relative' }}>
            <button
              className="adash-topbar-icon-btn"
              onClick={() => setNotifOpen(o => !o)}
              title="Pending approvals"
            >
              <svg width="17" height="17" fill="none" viewBox="0 0 24 24">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" stroke="#374151" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {notifCount > 0 && <span className="adash-notif-badge" />}
            </button>

            {/* ── notification panel ── */}
           {notifOpen && (
  <div style={{
    position: 'fixed',
    top: 60,
    ...(isMobile
      ? { left: 8, right: 8, width: 'auto', maxHeight: '75vh' }
      : { right: 16, left: 'auto', width: 360, maxHeight: 480 }
    ),
    background: '#fff',
    borderRadius: 14,
    boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
    border: '1px solid #e8ecf1',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    animation: 'topbarNotifSlide 0.18s ease'
  }}>
                {/* header */}
                <div style={{
                  background: 'linear-gradient(135deg, #1e3a6e, #1d4ed8)',
                  padding: '14px 18px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>🔔</span>
                    <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>Pending Approvals</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {notifCount > 0 && (
                      <span style={{
                        background: '#ef4444', color: '#fff',
                        borderRadius: 20, padding: '2px 8px',
                        fontSize: 11, fontWeight: 700
                      }}>{notifCount}</span>
                    )}
                    <button onClick={() => setNotifOpen(false)} style={{
                      background: 'rgba(255,255,255,0.15)', border: 'none',
                      color: '#fff', borderRadius: '50%', width: 24, height: 24,
                      cursor: 'pointer', fontSize: 16, display: 'flex',
                      alignItems: 'center', justifyContent: 'center'
                    }}>×</button>
                  </div>
                </div>

                {/* list */}
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {pendingList.length === 0 ? (
                    <div style={{
                      padding: '40px 20px', textAlign: 'center',
                      color: '#9ca3af', fontSize: 13
                    }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                      No pending approvals
                    </div>
                  ) : (
                    pendingList.map((req, i) => {
                      const name  = req.employeeName  || req.employee_name  || 'Employee';
                      const email = req.employeeEmail || req.employee_email || '';
                      const type  = leaveLabel(req.leaveType || req.leave_type);
                      const dur   = req.duration || `${req.daysRequested || req.days_requested || 1} day(s)`;
                      const date  = req.dateApplied || req.createdAt || '';
                      const prio  = req.prio || 'low';

                      return (
                        <div key={req.id || i} style={{
                          padding: '14px 18px',
                          borderBottom: i < pendingList.length - 1 ? '1px solid #f3f4f6' : 'none',
                          background: i % 2 === 0 ? '#fff' : '#fafbfc',
                          transition: 'background 0.15s',
                          cursor: 'default'
                        }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f0f7ff'}
                          onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}
                        >
                          {/* top row */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {/* avatar */}
                              <div style={{
                                width: 30, height: 30, borderRadius: '50%',
                                background: '#1d4ed8', color: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 11, fontWeight: 700, flexShrink: 0
                              }}>
                                {getInitials(name)}
                              </div>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{name}</div>
                                <div style={{ fontSize: 11, color: '#9ca3af' }}>{email}</div>
                              </div>
                            </div>
                            {/* <span style={{
                              fontSize: 10, fontWeight: 700,
                              color: prioColor(prio),
                              background: prioColor(prio) + '18',
                              padding: '2px 8px', borderRadius: 10,
                              textTransform: 'uppercase'
                            }}>{prio}</span> */}
                          </div>

                          {/* leave details */}
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                            <span style={{
                              fontSize: 11, background: '#eff6ff', color: '#1d4ed8',
                              border: '1px solid #bfdbfe', borderRadius: 6, padding: '2px 8px', fontWeight: 600
                            }}>{type}</span>
                            <span style={{
                              fontSize: 11, background: '#f3f4f6', color: '#374151',
                              borderRadius: 6, padding: '2px 8px'
                            }}>📅 {dur}</span>
                            {date && (
                              <span style={{
                                fontSize: 11, color: '#9ca3af',
                                borderRadius: 6, padding: '2px 4px'
                              }}>Applied: {date}</span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* footer */}
                {pendingList.length > 0 && (
                  <div style={{
                    padding: '12px 18px', borderTop: '1px solid #f3f4f6',
                    background: '#f8fafc', textAlign: 'center'
                  }}>
                    <a href="/Adminapprovals" style={{
                      fontSize: 13, fontWeight: 600, color: '#1d4ed8',
                      textDecoration: 'none'
                    }}>View All Approvals →</a>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── avatar (clickable) ── */}
          <div
            className="adash-avatar"
            onClick={() => setShowProfileModal(true)}
            title="Edit profile picture"
            style={{ cursor: 'pointer' }}
          >
            {renderAvatar()}
          </div>

          {/* logout */}
          <button className="adash-topbar-icon-btn" title="Logout"
            onClick={() => {
              saveCachedPic(null);
              saveCachedUser(null);
              window.location.href = '/?logout=success';
            }}>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" stroke="#374151" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </header>

      {menuOpen && (
        <div className="adash-mobile-nav" onClick={() => setMenuOpen(false)}>
          <div className="adash-mobile-nav-inner" onClick={e => e.stopPropagation()}>
            <Navbar />
          </div>
        </div>
      )}

      {/* ── Profile Picture Modal ── */}
      {showProfileModal && (
        <div
          onClick={closeProfileModal}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 99999, backdropFilter: 'blur(4px)',
            animation: 'topbarNotifSlide 0.2s ease'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 16,
              width: '90%', maxWidth: 460,
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            {/* Modal Header */}
            <div style={{
              padding: '18px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'linear-gradient(135deg, #1e3a6e, #1d4ed8)'
            }}>
              <h3 style={{
                margin: 0, fontSize: 18, fontWeight: 600, color: '#fff',
                display: 'flex', alignItems: 'center', gap: 10
              }}>
                <FaCamera /> Profile Picture
              </h3>
              <button
                onClick={closeProfileModal}
                style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none',
                  color: '#fff', borderRadius: '50%', width: 28, height: 28,
                  cursor: 'pointer', fontSize: 18,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '28px 24px', background: '#fff' }}>

              {/* Avatar preview */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
                {(previewImage || profilePic) ? (
                  <div style={{ textAlign: 'center' }}>
                    <img
                      src={previewImage || profilePic}
                      alt="Profile"
                      style={{
                        width: 140, height: 140, borderRadius: '50%',
                        objectFit: 'cover',
                        border: '4px solid #1d4ed8',
                        boxShadow: '0 8px 24px rgba(29,78,216,0.25)'
                      }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                    <p style={{ marginTop: 10, fontSize: 13, color: '#6b7280' }}>
                      {previewImage ? 'Preview' : 'Current Picture'}
                    </p>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      width: 140, height: 140, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #1e3a6e, #1d4ed8)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 48, color: '#fff', fontWeight: 700,
                      border: '4px solid #1d4ed8',
                      boxShadow: '0 8px 24px rgba(29,78,216,0.25)'
                    }}>
                      {getInitials(currentUser?.name)}
                    </div>
                    <p style={{ marginTop: 10, fontSize: 13, color: '#6b7280' }}>No Picture Set</p>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {previewImage ? (
                  <>
                    {/* Save */}
                    <button
                      onClick={handleUploadProfilePicture}
                      disabled={isUploading}
                      style={{
                        padding: '14px', border: 'none', borderRadius: 12,
                        background: 'linear-gradient(135deg, #1e3a6e, #1d4ed8)',
                        color: '#fff', fontSize: 15, fontWeight: 600,
                        cursor: isUploading ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                        opacity: isUploading ? 0.7 : 1,
                        boxShadow: '0 4px 12px rgba(29,78,216,0.35)',
                        transition: 'all 0.2s'
                      }}
                    >
                      <FaUpload />
                      {isUploading ? 'Saving…' : 'Save Profile Picture'}
                    </button>

                    {/* Cancel preview */}
                    <button
                      onClick={() => setPreviewImage(null)}
                      disabled={isUploading}
                      style={{
                        padding: '14px', borderRadius: 12,
                        background: '#fff', color: '#6b7280',
                        border: '2px solid #e5e7eb',
                        fontSize: 15, fontWeight: 600,
                        cursor: isUploading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    {/* Upload from device */}
                    <button
                      onClick={() => {
                        filePickerOpenRef.current = true;
                        profileFileInputRef.current?.click();
                      }}
                      disabled={isUploading}
                      style={{
                        padding: '14px', border: 'none', borderRadius: 12,
                        background: 'linear-gradient(135deg, #1e3a6e, #1d4ed8)',
                        color: '#fff', fontSize: 15, fontWeight: 600,
                        cursor: isUploading ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                        boxShadow: '0 4px 12px rgba(29,78,216,0.35)',
                        transition: 'all 0.2s'
                      }}
                    >
                      <FaUpload /> Upload from Device
                    </button>

                    {/* Remove — only shown when picture is set */}
                    {profilePic && (
                      <button
                        onClick={handleRemoveProfilePicture}
                        disabled={isUploading}
                        style={{
                          padding: '14px', border: 'none', borderRadius: 12,
                          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                          color: '#fff', fontSize: 15, fontWeight: 600,
                          cursor: isUploading ? 'not-allowed' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                          opacity: isUploading ? 0.7 : 1,
                          boxShadow: '0 4px 12px rgba(239,68,68,0.35)',
                          transition: 'all 0.2s'
                        }}
                      >
                        <FaTimes />
                        {isUploading ? 'Removing…' : 'Remove Picture'}
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Hidden file input */}
              <input
                ref={profileFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfileFileChange}
                style={{ display: 'none' }}
              />

              {/* Info */}
              <div style={{
                marginTop: 20, padding: 12,
                background: '#f3f4f6', borderRadius: 8,
                fontSize: 12, color: '#6b7280', textAlign: 'center'
              }}>
                📸 Supported formats: JPG, PNG, GIF · Maximum file size: 5MB
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes topbarNotifSlide {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default TopBar;
